import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Search, ShieldCheck, Star, Sparkles, Bug, Wrench, ArrowRight, AlertTriangle,
  Car, Disc, Truck, Battery, LifeBuoy, PhoneCall, Home as HomeIcon, Zap, Droplets, Key,
} from "lucide-react";
import Navbar from "@/components/Navbar";

const ICONS = {
  bug: Bug, sparkles: Sparkles, wrench: Wrench, droplets: Droplets, zap: Zap,
  key: Key, truck: Truck, disc: Disc, battery: Battery, "life-buoy": LifeBuoy,
  "phone-call": PhoneCall, car: Car,
};
const iconFor = (c) => ICONS[c?.icon] || Wrench;

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [postcode, setPostcode] = useState("");
  const nav = useNavigate();

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);

  const submit = (e) => {
    e.preventDefault();
    const qs = postcode ? `?postcode=${encodeURIComponent(postcode)}` : "";
    nav(`/request${qs}`);
  };

  const home = categories.filter((c) => c.group !== "vehicle");
  const vehicle = categories.filter((c) => c.group === "vehicle");

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="hero-bg">
        <div className="max-w-6xl mx-auto px-4 py-14 sm:py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 badge-verified mb-6" data-testid="hero-badge">
              <ShieldCheck size={14} /> Trusted local pros — home &amp; vehicle
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Trusted local help for your <span className="text-emerald-500">home</span> and your <span className="text-slate-900 underline decoration-red-500 decoration-4 underline-offset-4">vehicle</span>.
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">
              Book verified home services or get roadside &amp; breakdown help in minutes across the UK. Fair prices, real reviews, secure payments in GBP.
            </p>

            <form onSubmit={submit} className="mt-8 card !p-3 flex flex-col sm:flex-row gap-3 items-stretch">
              <div className="flex items-center gap-3 flex-1 px-4">
                <Search className="text-slate-400" size={20} />
                <input
                  data-testid="hero-postcode-input"
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  placeholder="Enter your postcode e.g. L1 8JQ"
                  className="w-full bg-transparent py-3 outline-none text-base"
                />
              </div>
              <button data-testid="hero-search-button" type="submit" className="btn-accent whitespace-nowrap">
                Find help
              </button>
            </form>

            <div className="mt-4">
              <Link
                to="/emergency"
                data-testid="hero-emergency-cta"
                className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white px-5 py-3 font-bold shadow-lg shadow-red-500/25 transition-transform hover:-translate-y-0.5"
              >
                <AlertTriangle size={18} /> Need Help Now — Emergency Service
              </Link>
            </div>

            <div className="mt-6 flex items-center gap-6 text-sm text-slate-600">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-500" /> Verified pros</span>
              <span className="inline-flex items-center gap-2"><Star size={16} className="text-amber-500" /> Real reviews</span>
              <span className="inline-flex items-center gap-2 hidden sm:inline-flex">£ Secure payments</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.15)]">
              <img
                src="https://images.pexels.com/photos/6050250/pexels-photo-6050250.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Handyman"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 border border-slate-200 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center font-bold">JM</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">Jamie · Handyman</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Star size={12} className="text-amber-500" /> 4.9 · 128 reviews · L1</div>
                </div>
                <span className="badge-verified"><ShieldCheck size={12} /> Verified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Need Help Now — full band */}
      <section className="border-y border-red-200 bg-red-50">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-red-600/10 text-red-700 px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <AlertTriangle size={14} /> Emergency dispatch
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3">
              Broken down or locked out? Get help in minutes.
            </h2>
            <p className="text-slate-700 mt-2">
              Flat tyre, jump start, tow, emergency plumber, electrician or locksmith — request a verified local pro right now.
            </p>
            <Link
              to="/emergency"
              data-testid="need-help-now-btn"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-bold shadow-lg shadow-red-500/25"
            >
              <AlertTriangle size={18} /> Need Help Now
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { name: "Flat tyre / Puncture", icon: Disc, slug: "flat-tyre-puncture-repair" },
              { name: "Breakdown Recovery", icon: Truck, slug: "breakdown-recovery" },
              { name: "Jump Start", icon: Battery, slug: "battery-jump-start" },
              { name: "Emergency Plumber", icon: Droplets, slug: "emergency-plumber" },
              { name: "Emergency Electrician", icon: Zap, slug: "emergency-electrician" },
              { name: "Emergency Locksmith", icon: Key, slug: "emergency-locksmith" },
            ].map((it) => (
              <Link
                key={it.slug}
                to={`/emergency?category=${it.slug}`}
                data-testid={`emg-quick-${it.slug}`}
                className="bg-white border border-red-100 rounded-2xl p-3 flex items-center gap-2 hover:border-red-300 transition"
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-100 text-red-700">
                  <it.icon size={16} />
                </span>
                <span className="text-sm font-semibold text-slate-800">{it.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Home services */}
      <section className="max-w-6xl mx-auto px-4 py-14 sm:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-600 inline-flex items-center gap-2">
              <HomeIcon size={12} /> Home Services
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">Popular around your home</h2>
          </div>
          <Link to="/request" data-testid="cats-view-all-home" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-slate-700 hover:text-emerald-600">
            View all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {home.filter((c) => c.featured).map((c) => {
            const Ico = iconFor(c);
            return (
              <Link
                key={c.id || c.slug}
                to={`/request?category=${c.slug}`}
                data-testid={`category-tile-${c.slug}`}
                className="group relative rounded-2xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 transition-transform duration-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.10)]"
              >
                <div className="relative h-56 overflow-hidden">
                  <img src={c.image_url} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur rounded-full px-3 py-1 text-xs font-bold text-slate-900">
                    <Ico size={14} className="text-emerald-500" /> {c.name}
                  </div>
                </div>
                <div className="p-5">
                  <div className="font-semibold text-slate-900 text-lg">{c.name}</div>
                  <p className="text-sm text-slate-600 mt-1">{c.description}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-emerald-600 font-semibold text-sm">
                    Browse pros <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Vehicle services */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-14 sm:py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-700 inline-flex items-center gap-2">
                <Car size={12} /> Vehicle &amp; Roadside
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">Help for your car, at home or on the road</h2>
            </div>
            <Link to="/request" data-testid="cats-view-all-vehicle" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-slate-700 hover:text-emerald-600">
              View all <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {vehicle.filter((c) => c.featured).map((c) => {
              const Ico = iconFor(c);
              return (
                <Link
                  key={c.id || c.slug}
                  to={`/request?category=${c.slug}`}
                  data-testid={`category-tile-${c.slug}`}
                  className="group relative rounded-2xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 transition-transform duration-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.10)]"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img src={c.image_url} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur rounded-full px-3 py-1 text-xs font-bold text-slate-900">
                      <Ico size={14} className="text-slate-900" /> {c.name}
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="font-semibold text-slate-900 text-lg">{c.name}</div>
                    <p className="text-sm text-slate-600 mt-1">{c.description}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-slate-900 font-semibold text-sm">
                      Browse pros <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-14 sm:py-20 grid md:grid-cols-3 gap-6">
          {[
            { t: "1. Search by postcode", d: "Enter your postcode and pick a home or vehicle service to see verified local pros." },
            { t: "2. Get a fair quote", d: "Describe the job or roadside issue and receive a clear quote in GBP." },
            { t: "3. Book and pay safely", d: "Pay through Stripe — funds are held until the job is done." },
          ].map((s, i) => (
            <div key={i} className="card">
              <div className="text-xs font-bold uppercase tracking-widest text-emerald-600">Step {i + 1}</div>
              <div className="font-display text-xl font-semibold mt-2">{s.t}</div>
              <p className="text-slate-600 mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-4 py-10 text-sm text-slate-500 flex flex-col sm:flex-row justify-between gap-4">
        <div>© {new Date().getFullYear()} FixNear · Trusted local help — home &amp; vehicle · UK</div>
        <div className="flex gap-6">
          <Link to="/signup?role=provider" className="hover:text-slate-900">Become a provider</Link>
          <Link to="/emergency" className="hover:text-red-600 font-semibold">Need help now</Link>
          <Link to="/login" className="hover:text-slate-900">Log in</Link>
        </div>
      </footer>
    </div>
  );
}
